import { useZoneEditorStore } from "../../state/zone-editor-store.js";

interface ZoneEditorToolbarProps {
  onSave: () => Promise<void>;
  onReprocess: () => Promise<void>;
  onDelete: () => void;
  isSaving: boolean;
}

export function ZoneEditorToolbar({
  onSave,
  onReprocess,
  onDelete,
  isSaving,
}: ZoneEditorToolbarProps) {
  const {
    toolMode,
    setToolMode,
    pageMatchingStrategy,
    setPageMatchingStrategy,
    selectedZoneFieldId,
    removeZone,
    zoneMappings,
    pages,
    isDirty,
  } = useZoneEditorStore();

  const totalZones = zoneMappings.filter((zm) => zm.zone !== null).length;
  const totalPages = pages.length;

  const handleDeleteZone = () => {
    if (selectedZoneFieldId) {
      removeZone(selectedZoneFieldId);
    }
  };

  const btnBase: React.CSSProperties = {
    padding: "5px 12px",
    fontSize: 13,
    borderRadius: 4,
    border: "1px solid #d1d5db",
    cursor: "pointer",
    background: "#fff",
    color: "#374151",
    fontWeight: 500,
    lineHeight: "20px",
  };

  const btnActive: React.CSSProperties = {
    ...btnBase,
    background: "#ea580c",
    borderColor: "#ea580c",
    color: "#fff",
  };

  const btnDanger: React.CSSProperties = {
    ...btnBase,
    background: "#fee2e2",
    borderColor: "#fca5a5",
    color: "#dc2626",
  };

  const btnPrimary: React.CSSProperties = {
    ...btnBase,
    background: isDirty && !isSaving ? "#ea580c" : "#f3f4f6",
    borderColor: isDirty && !isSaving ? "#ea580c" : "#e5e7eb",
    color: isDirty && !isSaving ? "#fff" : "#9ca3af",
    cursor: isDirty && !isSaving ? "pointer" : "not-allowed",
  };

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "8px 12px",
        borderBottom: "1px solid #e5e7eb",
        background: "#fff",
        flexShrink: 0,
      }}
    >
      {/* Tool mode */}
      <div style={{ display: "flex", gap: 2 }}>
        <button
          style={toolMode === "select" ? btnActive : btnBase}
          onClick={() => setToolMode("select")}
          title="Select tool"
        >
          Select
        </button>
        <button
          style={toolMode === "draw" ? btnActive : btnBase}
          onClick={() => setToolMode("draw")}
          title="Draw zone"
        >
          Draw
        </button>
      </div>

      {/* Divider */}
      <div style={{ width: 1, height: 24, background: "#e5e7eb" }} />

      {/* Page matching strategy */}
      <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#374151" }}>
        <span style={{ whiteSpace: "nowrap", color: "#6b7280" }}>Match by:</span>
        <select
          value={pageMatchingStrategy}
          onChange={(e) => setPageMatchingStrategy(e.target.value)}
          style={{
            fontSize: 12,
            padding: "4px 8px",
            border: "1px solid #d1d5db",
            borderRadius: 4,
            color: "#374151",
            background: "#fff",
          }}
        >
          <option value="fingerprint">Fingerprint</option>
          <option value="anchor">Anchor text</option>
          <option value="positional">Position</option>
          <option value="page_number">Page number</option>
        </select>
      </label>

      {/* Divider */}
      <div style={{ width: 1, height: 24, background: "#e5e7eb" }} />

      {/* Re-run OCR */}
      <button style={btnBase} onClick={onReprocess} title="Re-run OCR on template">
        Re-run OCR
      </button>

      {/* Delete zone (only when selected) */}
      {selectedZoneFieldId && (
        <button style={btnDanger} onClick={handleDeleteZone} title="Remove selected zone">
          Delete Zone
        </button>
      )}

      {/* Spacer */}
      <div style={{ flex: 1 }} />

      {/* Status */}
      <span style={{ fontSize: 12, color: "#6b7280", whiteSpace: "nowrap" }}>
        {totalZones} zone{totalZones !== 1 ? "s" : ""} across {totalPages} page{totalPages !== 1 ? "s" : ""}
      </span>

      {/* Divider */}
      <div style={{ width: 1, height: 24, background: "#e5e7eb" }} />

      {/* Replace Template */}
      <button style={btnBase} onClick={onDelete} title="Replace template document">
        Replace Template
      </button>

      {/* Save */}
      <button
        style={btnPrimary}
        onClick={onSave}
        disabled={!isDirty || isSaving}
        title={isDirty ? "Save zone mappings" : "No unsaved changes"}
      >
        {isSaving ? "Saving…" : "Save"}
      </button>
    </div>
  );
}
